/**
 * Stato del checkout che deve sopravvivere a un refresh, a un «indietro» dal
 * pagamento o a una risposta persa (R-48, R-63, R-72).
 *
 * - **Intento d'acquisto**: una chiave di idempotenza per carrello + dati del
 *   modulo. Lo stesso intento ritentato (doppio click, rete caduta, refresh)
 *   manda la stessa chiave e il server restituisce lo STESSO ordine; un
 *   carrello o un modulo diverso è un intento nuovo, con una chiave nuova.
 * - **Ordine in attesa di pagamento**: l'ordine aperto da questo intento, per
 *   riprenderne il pagamento senza crearne un altro.
 *
 * sessionStorage: resta nella scheda, sparisce chiudendola. Mai dati sensibili:
 * solo impronta, chiave e id dell'ordine.
 */

const INTENT_KEY = "gb_checkout_intent";

export interface CheckoutIntent {
  fingerprint: string;
  key: string;
  orderId?: string;
  orderNumber?: number | null;
  /** begin_checkout già emesso per questo intento (un ritentativo non è un nuovo checkout). */
  beginTracked?: boolean;
  createdAt: number;
}

function read(): CheckoutIntent | null {
  try {
    const raw = sessionStorage.getItem(INTENT_KEY);
    return raw ? (JSON.parse(raw) as CheckoutIntent) : null;
  } catch {
    return null;
  }
}

function write(intent: CheckoutIntent | null): void {
  try {
    if (intent) sessionStorage.setItem(INTENT_KEY, JSON.stringify(intent));
    else sessionStorage.removeItem(INTENT_KEY);
  } catch {
    /* storage non disponibile: si perde solo la ripresa, non l'ordine */
  }
}

function newKey(): string {
  try {
    if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  } catch {
    /* ripiego sotto */
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 14)}`;
}

/** Impronta stabile dell'acquisto: stessi prodotti, quantità e dati = stesso intento. */
export function purchaseFingerprint(input: unknown): string {
  const text = JSON.stringify(input);
  let h1 = 0x811c9dc5;
  let h2 = 0;
  for (let i = 0; i < text.length; i++) {
    const c = text.charCodeAt(i);
    h1 = Math.imul(h1 ^ c, 0x01000193) >>> 0;
    h2 = (h2 * 31 + c) >>> 0;
  }
  return `${h1.toString(16)}-${h2.toString(16)}-${text.length}`;
}

/** La chiave dell'intento: la stessa per lo stesso acquisto, nuova se cambia. */
export function intentFor(fingerprint: string): CheckoutIntent {
  const current = read();
  if (current && current.fingerprint === fingerprint) return current;
  const intent: CheckoutIntent = { fingerprint, key: newKey(), createdAt: Date.now() };
  write(intent);
  return intent;
}

export function rememberOrder(fingerprint: string, orderId: string, orderNumber: number | null): void {
  const current = read();
  if (current && current.fingerprint === fingerprint) {
    write({ ...current, orderId, orderNumber });
  }
}

/** Vero una sola volta per intento: il begin_checkout non si ripete a ogni ritentativo. */
export function markBeginCheckout(fingerprint: string): boolean {
  const current = read();
  if (!current || current.fingerprint !== fingerprint || current.beginTracked) return false;
  write({ ...current, beginTracked: true });
  return true;
}

/** L'ordine in attesa di pagamento aperto da questa scheda, se c'è. */
export function pendingCheckout(): CheckoutIntent | null {
  const current = read();
  return current && current.orderId ? current : null;
}

export function clearCheckoutIntent(orderId?: string): void {
  const current = read();
  if (!orderId || !current || current.orderId === orderId) write(null);
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Un order_id arrivato dall'URL entra in un percorso API solo se è un UUID (R-75). */
export function isOrderId(value: string | null | undefined): value is string {
  return !!value && UUID_RE.test(value);
}

export type PaymentState =
  | "awaiting_payment"
  | "processing"
  | "paid"
  | "failed"
  | "expired"
  | "cancelled"
  | "shipped"
  | "delivered"
  | "request_received"
  | "on_hold";

/** Lo stato verificato dal server; ricavato da `status` se il backend non lo manda ancora. */
export function paymentStateOf(o: { status: string; payment_state?: PaymentState | null }): PaymentState {
  if (o.payment_state) return o.payment_state;
  switch (o.status) {
    case "PAID": return "paid";
    case "SHIPPED": return "shipped";
    case "DELIVERED": return "delivered";
    case "CANCELLED": return "cancelled";
    default: return "awaiting_payment";
  }
}

export interface CheckoutConfig {
  contract_version: number;
  mode: "online" | "manual";
  online_available: boolean;
  min_amount_cents: number;
  max_amount_cents: number;
}
